/**
 * Stone Investment Blog Filters
 *
 * Legacy BlogFilters with Stone's editorial aesthetic:
 * square pill categories, greyscale palette, minimal sort.
 */

'use client';

import type { BlogFiltersProps } from '@routeroyal/theme-core';

const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

interface StoneBlogFiltersProps extends BlogFiltersProps {
  messages?: Record<string, unknown>;
}

export function StoneBlogFilters({
  categories,
  selectedCategory,
  onCategoryChange,
  sortOptions,
  sortBy = 'latest',
  onSortChange,
  messages,
  className = '',
}: StoneBlogFiltersProps) {
  const stoneMsg = messages?.stone as Record<string, unknown> | undefined;
  const blogMsg = stoneMsg?.blog as Record<string, unknown> | undefined;
  const filtersMsg = blogMsg?.filters as Record<string, unknown> | undefined;

  const resolvedSortOptions = sortOptions || [
    { label: safeGetString(filtersMsg?.latest, 'Latest'), value: 'latest' },
    { label: safeGetString(filtersMsg?.oldest, 'Oldest'), value: 'oldest' },
    { label: safeGetString(filtersMsg?.popular, 'Most Popular'), value: 'popular' },
  ];

  return (
    <div className={className}>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        {/* Category pills */}
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => onCategoryChange(undefined)}
              className={`
                px-5 py-2.5 text-sm tracking-wide transition-colors
                ${!selectedCategory
                  ? 'bg-stone-900 text-white'
                  : 'bg-white text-stone-700 border border-stone-300 hover:border-stone-500'
                }
              `}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {safeGetString(filtersMsg?.allCategories, 'All')}
            </button>

            {categories.map((category) => (
              <button
                key={category}
                onClick={() => onCategoryChange(category)}
                className={`
                  px-5 py-2.5 text-sm tracking-wide transition-colors
                  ${selectedCategory === category
                    ? 'bg-stone-900 text-white'
                    : 'bg-white text-stone-700 border border-stone-300 hover:border-stone-500'
                  }
                `}
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Sort dropdown */}
        {onSortChange && (
          <div className="flex items-center gap-3">
            <label
              htmlFor="stone-blog-sort"
              className="text-sm text-stone-500 tracking-wide"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {safeGetString(filtersMsg?.sortBy, 'Sort:')}
            </label>
            <select
              id="stone-blog-sort"
              value={sortBy}
              onChange={(e) => onSortChange(e.target.value)}
              className="
                appearance-none px-4 py-2.5
                bg-white border border-stone-300
                text-sm text-stone-900
                focus:outline-none focus:border-stone-500
                transition-colors cursor-pointer
              "
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              {resolvedSortOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
